import type { Canvas, FabricObject } from 'fabric';

type TViewportSceneServiceParams = {
    canvas: Canvas;
    getCanvasHeight: () => number;
    getCanvasWidth: () => number;
    // Zoom of the default view, derived by the scene from the canvas and
    // surface sizes. Read through a getter so a resize is picked up.
    getDefaultZoom: () => number;
    getImageRenderHeight: () => number;
    getImageRenderWidth: () => number;
};

const FOCUS_ELEMENT_SCALE_RATIO = 0.5;
const FOCUS_ELEMENT_MAX_SCALE = 0.7;

// Viewport-layout concerns extracted from FabricScene
// (displayImageFullCanvas, focusOnElement). Injected with getters so it
// always reads the current canvas / render dimensions and the current
// default zoom — all three change on resize.
class ViewportSceneService {
    private readonly _canvas: Canvas;
    private readonly _getCanvasHeight: () => number;
    private readonly _getCanvasWidth: () => number;
    private readonly _getDefaultZoom: () => number;
    private readonly _getImageRenderHeight: () => number;
    private readonly _getImageRenderWidth: () => number;

    constructor(params: TViewportSceneServiceParams) {
        this._canvas = params.canvas;
        this._getCanvasHeight = params.getCanvasHeight;
        this._getCanvasWidth = params.getCanvasWidth;
        this._getDefaultZoom = params.getDefaultZoom;
        this._getImageRenderHeight = params.getImageRenderHeight;
        this._getImageRenderWidth = params.getImageRenderWidth;
    }

    displayImageFullCanvas(): void {
        const imageRenderWidth = this._getImageRenderWidth();
        const imageRenderHeight = this._getImageRenderHeight();
        const canvasWidth = this._getCanvasWidth();
        const canvasHeight = this._getCanvasHeight();

        this._canvas.setZoom(this._getDefaultZoom());
        const canvasZoom = this._canvas.getZoom() ?? 1;

        const [zoom0, number1, number2, zoom3] = this._canvas.viewportTransform;
        this._canvas.setViewportTransform([
            zoom0,
            number1,
            number2,
            zoom3,
            // The print surface is the image-render size — the same source the zoom
            // factor above derives from. This used to read the bounds of
            // `getObjects('group').find((g) => g.id)`, which never matched the
            // working-zone group (it carries a `name`, no `id`) and instead picked
            // the first id-bearing overlay group — the locked-foreground one, sent
            // to the back. It stayed harmless only while that group was empty:
            // width 0 is falsy, so a fallback took over. Locking a field filled it,
            // and the canvas centre landed on the surface's top-left corner.
            canvasWidth / 2 - (imageRenderWidth / 2) * canvasZoom,
            canvasHeight / 2 - (imageRenderHeight / 2) * canvasZoom,
        ]);

        this._canvas.renderAll();
    }

    focusOnElement(element: FabricObject): void {
        const { x, y } = element.getCenterPoint();
        const bBox = element.getBoundingRect();
        const canvasWidth = this._getCanvasWidth();
        const canvasHeight = this._getCanvasHeight();
        const calcScalefactor =
            (bBox.width > bBox.height ? canvasWidth / bBox.width : canvasHeight / bBox.height) * FOCUS_ELEMENT_SCALE_RATIO;
        const scaleFactor = Math.min(calcScalefactor, FOCUS_ELEMENT_MAX_SCALE);
        const [, number1, number2] = this._canvas.viewportTransform;
        this._canvas.setViewportTransform([
            scaleFactor,
            number1,
            number2,
            scaleFactor,
            canvasWidth / 2 - x * scaleFactor,
            canvasHeight / 2 - y * scaleFactor,
        ]);
        this._canvas.renderAll();
    }
}

export { ViewportSceneService };
